/* eslint-disable no-nested-ternary */
import { useRef, useState } from 'react';
import { Box, Button, Chip, CircularProgress, IconButton, Menu, MenuItem } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { nanoid } from '@reduxjs/toolkit';
import { useLocation, useNavigate } from 'react-router';
import queryString from 'query-string';
import { useSelector } from 'react-redux';
import { CountryWithFlag } from 'ui/CountryWithFlag';
import { TFilter, TFilters } from 'services/constants/types';
import { ETableStructureType } from 'services/constants/tableStructureTypes';
import { CountrySelect } from 'ui/CountrySelect';
import { DateRangeModal } from 'ui/DateRangeModal';
import { DATES_ID } from 'services/constants/dates';
import { Search } from './Search';
import { TEasTableFilterPanelProps } from './types';

export const EasTableFilterPanel = <T,>({
	tableStructure,
	children,
	loading = false,
}: TEasTableFilterPanelProps<T>) => {
	const navigate = useNavigate();
	const location = useLocation();
	const { search } = useLocation();
	const navigateParams = queryString.parse(search);

	const filtersArray: TFilters = tableStructure.reduce(
		(acc, item) => {
			if (navigateParams[item.selector as string])
				acc.push({
					fieldName: item.selector as string,
					value: navigateParams[item.selector as string] as string,
				});
			return acc;
		},
		[] as { fieldName: string; value: string }[],
	);

	const refAddFilter = useRef<HTMLButtonElement>(null);
	const refNewFilter = useRef<HTMLButtonElement>(null);

	const [openDateRange, setOpenDateRange] = useState(false);
	const handleCloseDateRange = () => {
		setOpenDateRange(false);
	};

	const [newFilterIndex, setNewFilterIndex] = useState<number | null>(null);
	const [open, setOpen] = useState(false);
	const [openFilterList, setOpenFilterList] = useState(false);
	const handleClick = () => {
		setOpen(true);
	};
	const handleClose = () => {
		setOpen(false);
	};
	const handleCloseFilterList = () => {
		setOpenFilterList(false);
		setNewFilterIndex(null);
	};
	const handleNewFilter = (index: number) => {
		setNewFilterIndex(index);
		handleClose();
		setOpenFilterList(true);
	};

	const filtersNavigate = (newFiltersArray: TFilter[]) => {
		const searchTmp = newFiltersArray.reduce(
			(acc, item) => {
				if (item?.fieldName && item?.value !== null) {
					acc[String(item.fieldName)] = item.value;
				}
				return acc;
			},
			{} as { [key: string]: string | number | string[] | number[] },
		);
		if (navigateParams?.sortIndex && navigateParams?.sortDirection) {
			searchTmp.sortIndex = navigateParams.sortIndex as string;
			searchTmp.sortDirection = navigateParams.sortDirection as string;
		}
		if (navigateParams?.search) {
			searchTmp.search = navigateParams.search as string;
		}
		navigate({
			pathname: location.pathname,
			search: queryString.stringify(searchTmp),
		});
	};

	const handleSelectFilter = (index: number) => {
		if (newFilterIndex === null) return;
		const newFiltersArray = [...filtersArray];
		handleCloseFilterList();
		if (
			tableStructure[newFilterIndex].type === ETableStructureType.DATE &&
			tableStructure[newFilterIndex].filter?.[index][0] === DATES_ID.CUSTOM_RANGE
		) {
			setOpenDateRange(true);
			newFiltersArray.push({
				fieldName: tableStructure[newFilterIndex].selector as string,
				value: tableStructure[newFilterIndex].filter?.[index][0] ?? '',
			});
			// setNewFilterIndex(null);
			filtersNavigate(newFiltersArray);

			return;
		}
		// if (tableStructure[newFilterIndex].type === ETableStructureType.DATE_MONTH) {
		// 	setOpenYearMonthPiker(true);
		// 	return;
		// }

		const filterIndex = newFiltersArray?.findIndex?.(
			(item) => item?.fieldName === tableStructure[newFilterIndex].selector,
		);
		if (filterIndex > -1 && !!newFiltersArray && !!newFiltersArray?.[filterIndex]?.value) {
			// eslint-disable-next-line @typescript-eslint/ban-ts-comment
			// @ts-ignore: Unreachable code error
			newFiltersArray[filterIndex].value = tableStructure[newFilterIndex].filter?.[index][0] ?? '';
		} else {
			newFiltersArray.push({
				fieldName: tableStructure[newFilterIndex].selector as string,
				value: tableStructure[newFilterIndex].filter?.[index][0] ?? '',
			});
		}
		if (newFiltersArray) {
			setNewFilterIndex(null);
			filtersNavigate(newFiltersArray);
		}
	};

	const handleSelectCountry = (country: string) => {
		if (newFilterIndex === null) return;
		handleCloseFilterList();
		const newFiltersArray = [...filtersArray];
		const filterIndex = newFiltersArray?.findIndex?.(
			(item) => item?.fieldName === tableStructure[newFilterIndex].selector,
		);
		if (filterIndex > -1 && !!newFiltersArray && !!newFiltersArray?.[filterIndex]?.value) {
			// eslint-disable-next-line @typescript-eslint/ban-ts-comment
			// @ts-ignore: Unreachable code error
			newFiltersArray[filterIndex].value = country;
		} else {
			newFiltersArray.push({
				fieldName: tableStructure[newFilterIndex].selector as string,
				value: country,
			});
		}
		if (newFiltersArray) {
			setNewFilterIndex(null);
			filtersNavigate(newFiltersArray);
		}
	};

	const handleRemoveFilter = (index: number) => {
		const newFiltersArray = [...filtersArray];
		newFiltersArray.splice(index, 1);
		filtersNavigate(newFiltersArray);
	};

	const handleClearAll = () => {
		const newFiltersArray = [];
		const externalOrderIdItem = filtersArray.find((item) => item.fieldName === 'id');
		if (externalOrderIdItem) {
			newFiltersArray.push(externalOrderIdItem);
		}
		filtersNavigate(newFiltersArray);
	};

	return (
		<Box
			style={{
				padding: '8px',
				color: '#015079',
				fontWeight: 'bold',
				display: 'flex',
				flexDirection: 'row',
				justifyContent: 'space-between',
				gap: 2,
			}}
		>
			{children}
			{tableStructure.length > 0 ? (
				<Box sx={{ display: 'flex', gap: 1 }}>
					{filtersArray?.map?.((item, index) => {
						if (!item) return null;
						if (item.fieldName === 'id' || item.fieldName === 'external_order_id') return null;
						return (
							<Box
								key={nanoid()}
								color="primary"
								style={{
									display: 'inline-flex',
									alignItems: 'center',
									fontSize: '0.9rem',
									fontWeight: 500,
									textTransform: 'none',
									// background: '#00000015',
									borderRadius: '12px',
									padding: '2px 10px',
									gap: 3,
									border: '1px solid #016193',
								}}
							>
								{tableStructure?.find?.((i) => i.selector === item.fieldName)?.name}:{' '}
								<span style={{ fontWeight: 'bolder', marginLeft: '5px' }}>
									{item.fieldName === 'deliveryCountry' ? (
										<CountryWithFlag
											countryCode={String(item.value)}
											size="small"
											sx={{ fontWeight: 'bolder' }}
										/>
									) : (
										(tableStructure
											?.find?.((i) => i.selector === item.fieldName)
											?.filter?.find?.((i: string[]) => i[0] === item.value)?.[1] ?? item.value)
									)}
								</span>
								<IconButton style={{ padding: 0 }} onClick={() => handleRemoveFilter(index)}>
									<CloseIcon sx={{ fontSize: '1rem', fontWeight: 'bold' }} color="primary" />
								</IconButton>
							</Box>
						);
					})}
					<Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
						{newFilterIndex !== null && (
							<Button
								ref={refNewFilter}
								color="primary"
								style={{
									fontSize: '0.9rem',
									fontWeight: 500,
									padding: '2px 10px',
									textTransform: 'none',
									borderRadius: '12px',
									border: '1px dashed #016193',
									display: 'flex',
									alignItems: 'center',
									gap: 1,
								}}
							>
								{tableStructure[newFilterIndex].name}{' '}
								<ExpandMoreIcon fontSize="small" sx={{ mt: '3px' }} />
							</Button>
						)}
						{(tableStructure?.filter((item) => item.filterable)?.length ?? 0) > 0 && (
							<Button
								ref={refAddFilter}
								color="primary"
								sx={{
									fontWeight: 550,
									fontSize: '0.9rem',
									padding: '2px 10px',
									textTransform: 'none',
									borderRadius: '12px',
									border: '1px dashed #016193',
								}}
								onClick={handleClick}
							>
								Add filter +
							</Button>
						)}{' '}
						<Menu
							anchorEl={refAddFilter.current}
							open={open}
							onClose={handleClose}
							MenuListProps={{
								'aria-labelledby': 'basic-button',
							}}
							sx={{
								'& .MuiMenu-paper': { borderRadius: '12px' },
							}}
						>
							{tableStructure?.map?.((item, index) => {
								if (!item.filterable) return null;
								return (
									<MenuItem
										key={nanoid()}
										onClick={() => handleNewFilter(index)}
										color="primary"
										sx={{
											color: 'primary.main',
											fontWeight: 'bold',
										}}
									>
										{item.name}
									</MenuItem>
								);
							})}
						</Menu>
						<Menu
							anchorEl={refAddFilter.current}
							open={openFilterList}
							onClose={handleCloseFilterList}
							MenuListProps={{
								'aria-labelledby': 'basic-button',
							}}
							sx={{
								'& .MuiMenu-paper': { borderRadius: '12px', marginLeft: '-90px' },
							}}
						>
							{tableStructure?.[newFilterIndex ?? 0].type === ETableStructureType.COUNTRY_NAME ||
							tableStructure?.[newFilterIndex ?? 0].type === ETableStructureType.COUNTRY_CODE ? (
								<CountrySelect onChange={handleSelectCountry} />
							) : (
								(tableStructure?.[newFilterIndex ?? 0]?.filter?.map?.(
									(item: string[], index: number) => {
										if (!item) return null;
										return (
											<MenuItem
												key={nanoid()}
												onClick={() => handleSelectFilter(index)}
												color="primary"
												sx={{ color: 'primary.main', fontWeight: 'bold' }}
											>
												{item[1]}
											</MenuItem>
										);
									},
								) ?? null)
							)}
						</Menu>
					</Box>

					{filtersArray.some((item) => !!item && item.fieldName !== 'external_order_id') && (
						<Button
							color="primary"
							style={{
								fontWeight: 'bold',
								textTransform: 'none',
								padding: '2px 10px',
								borderRadius: '12px',
								textDecoration: 'underline',
							}}
							onClick={handleClearAll}
						>
							Clear all
						</Button>
					)}
				</Box>
			) : (
				<Box />
			)}
			<Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1 }}>
				{loading && (
					<Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
						<CircularProgress size={24} />
					</Box>
				)}
				{tableStructure?.find((item) => item.searchable) && (
					<Search
						searchParams={tableStructure
							?.filter((item) => item.searchable)
							.map((i) => i.name)
							?.join(', ')}
					/>
				)}
			</Box>
			<DateRangeModal
				open={openDateRange}
				setClose={handleCloseDateRange}
				selector={tableStructure?.[newFilterIndex ?? 0]?.selector as string}
			/>
		</Box>
	);
};
